import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  Snackbar,
  Typography,
  Paper,
  alpha,
  Stack,
  useTheme
} from "@mui/material";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  Edit as EditIcon,
  Dashboard as DashboardIcon,
  Image as ImageIcon
} from "@mui/icons-material";
import { tokens } from "../../theme";
import { authOnlyHeaders, siteCmsUrls } from "../../api/siteCms";
import { uploadCmsImageToS3 } from "../../utils/s3ImageUpload";

const HomeImagePage = () => {
  const theme = useTheme();
  const baseColors = tokens(theme.palette.mode);

  // Using the same color scheme as in the About Us component
  const colors = {
    ...baseColors,
    blueAccent: {
      100: "#d9eefb",
      200: "#b3ddf7",
      300: "#8dcbf4",
      400: "#67baf0",
      500: "#22a3f1",
      600: "#1b82c1",
      700: "#146291",
      800: "#0e4160",
      900: "#072030",
    },
  };

  const [imageURL, setImageURL] = useState("");
  const [selectedImage, setSelectedImage] = useState(null);
  const [previewImage, setPreviewImage] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [uploading, setUploading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchImageData = async () => {
      try {
        const response = await axios.get(siteCmsUrls.homeHero);
        const imageData = response.data.imageData;
        if (imageData.length > 0) {
          setImageURL(imageData[0].imageURL);
        }
      } catch (err) {
        setError(err);
        setSnackbarMessage("Error fetching Home Page image");
        setSnackbarOpen(true);
      } finally {
        setLoading(false);
      }
    };

    fetchImageData();
  }, []);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedImage(file);
      setPreviewImage(URL.createObjectURL(file));
    }
  };

  const handleImageUpload = async () => {
    if (!selectedImage) return;

    try {
      setUploading(true);
      const imageURL = await uploadCmsImageToS3(selectedImage);
      await axios.put(
        siteCmsUrls.homeHero,
        { imageURL },
        { headers: authOnlyHeaders() }
      );

      setSnackbarMessage("Home page image updated successfully!");
      setSnackbarOpen(true);
      setSelectedImage(null);
      
      // Refresh the current image
      const response = await axios.get(siteCmsUrls.homeHero);
      const imageData = response.data.imageData;
      if (imageData.length > 0) {
        setImageURL(imageData[0].imageURL);
      }
      setPreviewImage("");
    } catch (error) {
      setSnackbarMessage("Failed to update the image.");
      setSnackbarOpen(true);
      console.log("Error uploading image:", error);
    } finally {
      setUploading(false);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box m="20px" height="80vh" overflow="auto" paddingRight="20px">
      {/* Header Section - Styled similarly to the About Us header */}
      <Box
        mb={3}
        p={3}
        borderRadius={2}
        sx={{
          background: `linear-gradient(90deg, ${colors.blueAccent[500]} 0%, ${colors.blueAccent[400]} 100%)`,
          boxShadow: `0 4px 12px rgba(0, 0, 0, 0.1)`,
          border: `1px solid rgba(255, 255, 255, 0.3)`,
        }}
      >
        <Box display="flex" alignItems="center" gap={1} mb={1}>
          <DashboardIcon sx={{ color: "white", fontSize: 28 }} />
          <Typography variant="h3" fontWeight="bold" color="white">
            Home Page Image
          </Typography>
        </Box>
        <Typography variant="body1" color="white">
          Manage your homepage banner image
        </Typography>
      </Box>

      {/* Content Section */}
      <Grid container spacing={3}>
        {/* Current Image Section */}
        <Grid item xs={12} md={6}>
          <Paper
            sx={{
              p: 3,
              backgroundColor: "#ffffff",
              boxShadow: `0 10px 30px rgba(0, 0, 0, 0.05)`,
              border: `1px solid #e0e0e0`,
              borderRadius: 2,
              transition: "transform 0.2s ease-in-out",
              "&:hover": {
                transform: "translateY(-4px)",
                backgroundColor: "#ffffff",
              },
              height: "100%",
            }}
          >
            <Stack direction="row" spacing={2} alignItems="flex-start">
              <Box
                sx={{
                  backgroundColor: alpha(colors.blueAccent[500], 0.2),
                  borderRadius: "8px",
                  p: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <ImageIcon
                  sx={{
                    color: colors.blueAccent[500],
                    fontSize: 24,
                  }}
                />
              </Box>
              <Box>
                <Typography
                  variant="h5"
                  fontWeight="bold"
                  color="#333333"
                  mb={1}
                >
                  Current Home Page Image
                </Typography>
              </Box>
            </Stack>
            
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                mt: 3,
                padding: 2,
                border: `1px dashed ${alpha(colors.blueAccent[500], 0.5)}`,
                borderRadius: 1,
                backgroundColor: alpha(colors.blueAccent[100], 0.1),
                height: '250px',
              }}
            >
              {imageURL ? (
                <img
                  src={imageURL}
                  alt="Home Page Banner"
                  style={{
                    maxWidth: '100%',
                    maxHeight: '100%',
                    objectFit: 'contain',
                  }}
                />
              ) : (
                <Box 
                  display="flex" 
                  alignItems="center" 
                  justifyContent="center"
                  height="100%"
                  flexDirection="column"
                >
                  <ImageIcon sx={{ fontSize: 60, color: alpha(colors.blueAccent[500], 0.5) }} />
                  <Typography color="textSecondary" mt={1}>
                    No image available
                  </Typography>
                </Box>
              )}
            </Box>
            
            <Box mt={3} display="flex" justifyContent="center">
              <Button
                variant="contained"
                component="label"
                startIcon={<EditIcon />}
                sx={{
                  backgroundColor: colors.blueAccent[500],
                  color: "white",
                  fontSize: "14px",
                  padding: "10px 20px",
                  "&:hover": {
                    backgroundColor: colors.blueAccent[600],
                  },
                }}
              >
                Change Image
                <input type="file" hidden onChange={handleImageChange} />
              </Button>
            </Box>
          </Paper>
        </Grid>

        {/* Preview Image Section - Only show when an image is selected */}
        {previewImage && (
          <Grid item xs={12} md={6}>
            <Paper
              sx={{
                p: 3,
                backgroundColor: "#ffffff",
                boxShadow: `0 10px 30px rgba(0, 0, 0, 0.05)`,
                border: `1px solid #e0e0e0`,
                borderRadius: 2,
                transition: "transform 0.2s ease-in-out",
                "&:hover": {
                  transform: "translateY(-4px)",
                  backgroundColor: "#ffffff",
                },
                height: "100%",
              }}
            >
              <Stack direction="row" spacing={2} alignItems="flex-start">
                <Box
                  sx={{
                    backgroundColor: alpha(colors.blueAccent[500], 0.2),
                    borderRadius: "8px",
                    p: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <ImageIcon
                    sx={{
                      color: colors.blueAccent[500],
                      fontSize: 24,
                    }}
                  />
                </Box>
                <Box>
                  <Typography
                    variant="h5"
                    fontWeight="bold"
                    color="#333333"
                    mb={1}
                  >
                    Selected Image Preview
                  </Typography>
                </Box>
              </Stack>
              
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  mt: 3,
                  padding: 2,
                  border: `1px dashed ${alpha(colors.blueAccent[500], 0.5)}`,
                  borderRadius: 1,
                  backgroundColor: alpha(colors.blueAccent[100], 0.1),
                  height: '250px',
                }}
              >
                <img
                  src={previewImage}
                  alt="Preview Banner"
                  style={{
                    maxWidth: '100%',
                    maxHeight: '100%',
                    objectFit: 'contain',
                  }}
                />
              </Box>
              
              <Box mt={3} display="flex" justifyContent="center">
                <Button
                  variant="contained"
                  onClick={handleImageUpload}
                  disabled={uploading}
                  sx={{
                    backgroundColor: colors.blueAccent[500],
                    color: "white",
                    fontSize: "14px",
                    padding: "10px 20px",
                    "&:hover": {
                      backgroundColor: colors.blueAccent[600],
                    },
                  }}
                >
                  {uploading ? "Saving..." : "Save Changes"}
                </Button>
              </Box>
            </Paper>
          </Grid>
        )}
      </Grid>

      {/* Upload Progress Indicator */}
      {uploading && (
        <Box display="flex" justifyContent="center" mt={3}>
          <CircularProgress />
        </Box>
      )}

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        message={snackbarMessage}
      />
    </Box>
  );
};

export default HomeImagePage;